import { useState, type ReactNode } from "react";

import {
  useAssignments,
  useAttempts,
  useDashboardStats,
  type AttemptSummary,
  type DashboardStats,
} from "@/api/tests-api";

import { PanelConsole } from "./PanelConsole";

/**
 * The staff landing dashboard.
 *
 * Six count tiles over ALL sittings, from a single aggregate endpoint
 * rather than a page of the list -- a tile that counted only what
 * happened to be on screen would be worse than no tile. Each one carries
 * the same meaning as the Assessments filter of the same name, so a
 * number here and the list it stands for always agree.
 *
 * Below them, four worklists: the things that actually want a reviewer's
 * attention, each row clicking through to the sitting itself.
 *
 * `onOpenAssessments` lets a tile send the reader straight into the
 * Assessments view pre-filtered to that status. It is optional: the
 * dashboard is still readable on its own.
 */

interface Tile {
  key: string;
  label: string;
  /**
   * Which Assessments status filter this tile opens, or null when the
   * Assessments view cannot show what the tile counts. See `queued`.
   */
  filter: string | null;
  value: (s: DashboardStats) => number;
  /** The one that is a call to action reads in the accent; the rest are quiet. */
  accent?: boolean;
}

const TILES: Tile[] = [
  { key: "in_progress", label: "In progress now", filter: "in_progress", value: (s) => s.in_progress },
  {
    key: "awaiting",
    label: "Awaiting valuation",
    filter: "awaiting",
    value: (s) => s.awaiting_valuation,
    accent: true,
  },
  { key: "scored", label: "Scored", filter: "scored", value: (s) => s.scored },
  {
    key: "queued",
    label: "Queued",
    // Linked again as of 2026-08-25. It was deliberately NOT linked
    // while the Assessments view's Queued filter was an attempt query
    // (`attempt_status=NOT_STARTED`), because this tile counts
    // ASSIGNMENTS nobody has attended and that list could not show one
    // -- the tile read 1 and the list was empty. That filter now reads
    // the assignments list and the same `has_begun` predicate this
    // count uses, so the two agree and the link is safe.
    filter: "queued",
    value: (s) => s.queued,
  },
  { key: "expired", label: "Expired", filter: "expired", value: (s) => s.expired },
  { key: "total", label: "All sittings", filter: "all", value: (s) => s.total },
];

// ---------------------------------------------------------------------
// Shared list furniture
// ---------------------------------------------------------------------

/** How many rows each worklist shows before deferring to the full view. */
const PREVIEW_ROWS = 6;

function when(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function who(name: string | null, fallback: string | null): string {
  return name?.trim() || fallback?.trim() || "Unknown candidate";
}

/**
 * One worklist card.
 *
 * Deliberately a dumb shell: every list below owns its own query, so a
 * slow or failed one cannot blank the others. A list with nothing in it
 * still renders, saying so -- "no rows" and "not loaded" are different
 * facts and a reviewer needs to tell them apart.
 */
function ListCard({
  cy,
  title,
  blurb,
  isLoading,
  isError,
  empty,
  more,
  children,
}: {
  cy: string;
  title: string;
  blurb: string;
  isLoading: boolean;
  isError: boolean;
  empty: string;
  /** "View all" affordance, when a fuller view of this list exists. */
  more?: { label: string; onClick: () => void } | undefined;
  children: ReactNode;
}) {
  const hasRows = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <section
      data-cy={cy}
      className="flex flex-col rounded-xl border border-border bg-background-card shadow-theme-sm"
    >
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="mt-0.5 text-xs text-foreground-muted">{blurb}</p>
        </div>
        {more ? (
          <button
            type="button"
            data-cy={`${cy}-more`}
            onClick={more.onClick}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-accent-hover hover:bg-accent-light"
          >
            {more.label}
          </button>
        ) : null}
      </header>
      <div className="flex-1">
        {isLoading ? (
          <p className="px-4 py-4 text-sm text-foreground-muted">Loading…</p>
        ) : isError ? (
          <p className="px-4 py-4 text-sm text-status-urgent">
            Could not load this list.
          </p>
        ) : !hasRows ? (
          <p className="px-4 py-4 text-sm text-foreground-muted">{empty}</p>
        ) : (
          <ul className="divide-y divide-border">{children}</ul>
        )}
      </div>
    </section>
  );
}

/**
 * A row standing for one ATTEMPT. Clicking opens the same full screening
 * the Assessments list opens, so "view details" means one thing in this
 * app rather than two.
 */
function AttemptRow({
  row,
  meta,
  onOpen,
}: {
  row: AttemptSummary;
  meta: string;
  onOpen: (row: AttemptSummary) => void;
}) {
  return (
    <li>
      <button
        type="button"
        data-cy={`dash-row-${row.id}`}
        onClick={() => onOpen(row)}
        className="flex w-full items-baseline justify-between gap-3 px-4 py-2.5 text-left transition-colors hover:bg-table-row-hover"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm text-foreground">
            {who(row.candidate_name, row.candidate_username)}
          </span>
          <span className="block truncate text-xs text-foreground-muted">
            {row.definition_title}
          </span>
        </span>
        <span className="shrink-0 text-xs tabular-nums text-foreground-muted">
          {meta}
        </span>
      </button>
    </li>
  );
}

/** Minutes:seconds, for a countdown that is still running. */
function remaining(seconds: number | null): string {
  if (seconds === null || seconds < 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")} left`;
}

// ---------------------------------------------------------------------

export function DashboardPanel({
  onOpenAssessments,
}: {
  onOpenAssessments?: (statusFilter: string) => void;
}) {
  const stats = useDashboardStats();

  // The full screening, opened from any attempt row on this page.
  // Same single door as the Assessments list -- a second, lighter review
  // surface reachable only from here would be exactly the split the
  // Assessments list just stopped having.
  const [open, setOpen] = useState<{
    attemptId: string;
    candidateId: string;
    assignmentId: string | null;
    name: string | null;
    examTitle: string | null;
  } | null>(null);

  // Four independent queries rather than one dashboard payload. Each is
  // the SAME call the Assessments view makes for that filter, so the two
  // screens cannot drift into disagreeing about what "awaiting" means.
  const awaiting = useAttempts({
    scope: "all",
    reviewState: "awaiting",
    pageSize: PREVIEW_ROWS,
  });
  const ongoing = useAttempts({
    scope: "all",
    attemptStatus: "IN_PROGRESS",
    pageSize: PREVIEW_ROWS,
  });
  const scored = useAttempts({
    scope: "all",
    reviewState: "scored",
    pageSize: PREVIEW_ROWS,
  });
  // Queued comes from the ASSIGNMENTS list, which is the only place a
  // never-opened exam exists at all. `has_begun` is the backend's own
  // predicate -- false both when no attempt exists and when one exists
  // that never left NOT_STARTED, which is exactly "not attended".
  const assignments = useAssignments();
  const queued = (assignments.data ?? [])
    .filter((a) => !a.has_begun)
    .slice(0, PREVIEW_ROWS);

  const openReview = (row: AttemptSummary) =>
    setOpen({
      attemptId: row.id,
      candidateId: row.candidate_user_id,
      assignmentId: row.test_assignment_id,
      name: row.candidate_name ?? row.candidate_username ?? null,
      examTitle: row.definition_title,
    });

  const go = (filter: string) => () => onOpenAssessments?.(filter);
  const viewAll = (filter: string) =>
    onOpenAssessments ? { label: "View all", onClick: go(filter) } : undefined;

  return (
    <div data-cy="dashboard-panel" className="flex flex-col gap-6">
      <section>
        <h2 className="text-sm font-semibold text-foreground">At a glance</h2>
        <p className="mt-1 text-xs text-foreground-muted">
          Every sitting, by where it is in the process. Numbers refresh on
          their own.
        </p>

        {stats.isLoading ? (
          <p className="mt-4 text-sm text-foreground-muted">Loading…</p>
        ) : stats.isError ? (
          <p className="mt-4 text-sm text-status-urgent">
            Could not load the dashboard.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {TILES.map((tile) => {
              const value = stats.data ? tile.value(stats.data) : 0;
              const interactive = !!onOpenAssessments && tile.filter !== null;
              return (
                <button
                  key={tile.key}
                  type="button"
                  data-cy={`dashboard-tile-${tile.key}`}
                  disabled={!interactive}
                  onClick={() =>
                    tile.filter !== null && onOpenAssessments?.(tile.filter)
                  }
                  className={[
                    "flex flex-col items-start rounded-xl border bg-background-card p-4 text-left shadow-theme-sm transition-colors",
                    interactive ? "hover:bg-table-row-hover" : "cursor-default",
                    tile.accent && value > 0
                      ? "border-accent"
                      : "border-border",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "text-3xl font-semibold tabular-nums",
                      tile.accent && value > 0
                        ? "text-accent-hover"
                        : "text-foreground",
                    ].join(" ")}
                  >
                    {value}
                  </span>
                  <span className="mt-1 text-xs font-medium uppercase tracking-wide text-foreground-muted">
                    {tile.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* The worklists. Order is the reviewer's own priority: what needs
          scoring, then what is happening right now, then what has not
          started, then what is already done. */}
      <section className="grid gap-4 xl:grid-cols-2">
        <ListCard
          cy="dash-list-awaiting"
          title="Pending assessment"
          blurb="Finished sittings with no overall verdict yet."
          isLoading={awaiting.isLoading}
          isError={awaiting.isError}
          empty="Nothing is waiting to be scored."
          more={viewAll("awaiting")}
        >
          {(awaiting.data?.items ?? []).map((row) => (
            <AttemptRow
              key={row.id}
              row={row}
              meta={when(row.submitted_at)}
              onOpen={openReview}
            />
          ))}
        </ListCard>

        <ListCard
          cy="dash-list-ongoing"
          title="Sitting now"
          blurb="Candidates with the clock running."
          isLoading={ongoing.isLoading}
          isError={ongoing.isError}
          empty="Nobody is sitting an exam."
          more={viewAll("in_progress")}
        >
          {(ongoing.data?.items ?? []).map((row) => (
            <AttemptRow
              key={row.id}
              row={row}
              meta={remaining(row.seconds_remaining)}
              onOpen={openReview}
            />
          ))}
        </ListCard>

        <ListCard
          cy="dash-list-queued"
          title="Assigned, not opened"
          blurb="Sent to a candidate who has not attended yet. Nothing to review until they do."
          isLoading={assignments.isLoading}
          isError={assignments.isError}
          empty="Every assigned exam has been attended."
          more={viewAll("queued")}
        >
          {queued.map((a) => (
            <li
              key={a.id}
              data-cy={`dash-queued-${a.id}`}
              className="flex items-baseline justify-between gap-3 px-4 py-2.5"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground">
                  {who(a.candidate_name, null)}
                </span>
                <span className="block truncate text-xs text-foreground-muted">
                  {a.definition_title ?? "Untitled exam"}
                </span>
              </span>
              <span className="shrink-0 text-xs tabular-nums text-foreground-muted">
                assigned {when(a.created_at)}
              </span>
            </li>
          ))}
        </ListCard>

        <ListCard
          cy="dash-list-scored"
          title="Already scored"
          blurb="Sittings with an overall verdict recorded."
          isLoading={scored.isLoading}
          isError={scored.isError}
          empty="Nothing has been scored yet."
          more={viewAll("scored")}
        >
          {(scored.data?.items ?? []).map((row) => (
            <AttemptRow
              key={row.id}
              row={row}
              meta={
                row.overall_score !== null
                  ? `${row.overall_score}${row.overall_max_score !== null ? `/${row.overall_max_score}` : ""}`
                  : when(row.submitted_at)
              }
              onOpen={openReview}
            />
          ))}
        </ListCard>
      </section>

      {open ? (
        <PanelConsole
          candidateId={open.candidateId}
          candidateName={open.name}
          attemptId={open.attemptId}
          examTitle={open.examTitle}
          {...(open.assignmentId ? { assignmentId: open.assignmentId } : {})}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
